/**
 * AIInsightsPanel — правая панель карты:
 *   - Header: "ИИ-агроном · {date}"
 *   - 3 priority card: 🟢 N растут / 🟡 N замедлились / 🔴 N критично
 *   - Bottom CTA: "✨ Спросить про конкретное поле"
 *
 * Spec: AI-first narrative, primary value of redesign. Backend endpoint
 * /api/v1/dashboard/insights ещё не готов — см. useAIInsights mock.
 */
import { Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useMapStore } from '@/lib/map/useMapStore';
import { useAIInsights, type AICriticalField } from '@/lib/map/useAIInsights';
import { DraggablePanel } from './DraggablePanel';
import { cn } from '@/lib/utils';

interface AIInsightsPanelProps {
  mobile?: boolean;
  /** Кастомный handler для CTA (обычно открывает AIAssistant с pre-filled query). */
  onAskAI?: () => void;
  /** Кастомный handler для click on critical field. */
  onSelectField?: (fieldId: string) => void;
}

function formatDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function CriticalFieldRow({
  field,
  onClick,
}: {
  field: AICriticalField;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left px-2 py-2 rounded-md hover:bg-accent transition-colors"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-foreground truncate">
          {field.name}
        </span>
        <span
          className={cn(
            'text-xs font-mono shrink-0',
            (field.ndvi ?? 0) < 0.3 ? 'text-destructive' : 'text-accent-brand',
          )}
        >
          NDVI {typeof field.ndvi === 'number' ? field.ndvi.toFixed(2) : '—'}
        </span>
      </div>
      <div className="text-xs text-muted-foreground mt-1">{field.reason}</div>
    </button>
  );
}

export function AIInsightsPanel({
  mobile = false,
  onAskAI,
  onSelectField,
}: AIInsightsPanelProps) {
  const selectedDate = useMapStore((s) => s.selectedDate);
  const setSelectedFieldId = useMapStore((s) => s.setSelectedFieldId);
  const { data, isLoading, isError } = useAIInsights(selectedDate);

  const handleSelectField = (fieldId: string) => {
    setSelectedFieldId(fieldId);
    onSelectField?.(fieldId);
  };

  return (
    <DraggablePanel
      panelId="ai"
      title={
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-accent-brand" />
          <span>ИИ-агроном</span>
          <span className="text-xs text-muted-foreground font-normal">
            · {formatDate(selectedDate)}
          </span>
        </div>
      }
      disabled={mobile}
      className={cn(!mobile && 'w-[320px]')}
    >
      <div className="p-3 space-y-3">
        {/* Three priority cards */}
        <div className="grid grid-cols-3 gap-2">
          {isLoading && (
            <>
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </>
          )}
          {data && (
            <>
              <PriorityCard
                count={data.good}
                label="Растут"
                tone="good"
              />
              <PriorityCard
                count={data.warning}
                label="Замедл."
                tone="warning"
              />
              <PriorityCard
                count={data.bad}
                label="Критич."
                tone="bad"
              />
            </>
          )}
          {isError && (
            <div className="col-span-3 text-xs text-destructive">
              Не удалось загрузить инсайты
            </div>
          )}
        </div>

        {/* Narrative */}
        {data?.narrative && (
          <p className="text-xs text-muted-foreground leading-relaxed">
            {data.narrative}
          </p>
        )}

        {/* Critical fields list */}
        {data && data.critical_fields.length > 0 && (
          <ScrollArea className="max-h-[180px] -mx-1">
            <div className="space-y-1 px-1">
              {data.critical_fields.map((f) => (
                <CriticalFieldRow
                  key={f.id}
                  field={f}
                  onClick={() => handleSelectField(f.id)}
                />
              ))}
            </div>
          </ScrollArea>
        )}

        {/* CTA */}
        <Button
          type="button"
          onClick={onAskAI}
          className="w-full gap-2"
          variant="default"
        >
          <Sparkles className="h-4 w-4" />
          Спросить про конкретное поле
        </Button>
      </div>
    </DraggablePanel>
  );
}

function PriorityCard({
  count,
  label,
  tone,
}: {
  count: number;
  label: string;
  tone: 'good' | 'warning' | 'bad';
}) {
  const toneClass =
    tone === 'good'
      ? 'bg-ndvi-good/15 text-ndvi-good border-ndvi-good/30'
      : tone === 'warning'
        ? 'bg-ndvi-warning/15 text-ndvi-warning border-ndvi-warning/30'
        : 'bg-ndvi-bad/15 text-ndvi-bad border-ndvi-bad/30';
  const dot =
    tone === 'good'
      ? 'bg-ndvi-good'
      : tone === 'warning'
        ? 'bg-ndvi-warning'
        : 'bg-ndvi-bad';
  return (
    <div className={cn('rounded-lg border px-2 py-2 text-center', toneClass)}>
      <div className="flex items-center justify-center gap-1">
        <span className={cn('h-1.5 w-1.5 rounded-full', dot)} />
        <span className="text-lg font-bold leading-none">{count}</span>
      </div>
      <div className="text-xs mt-1 uppercase tracking-wide opacity-80">
        {label}
      </div>
    </div>
  );
}
